/**
 * ops 域 Zod 共享契约 — ADM-OPS-S001~S006
 */

import { z } from 'zod';
import { SettlementChannelSchema } from './payment-methods';
import { LanguageCodeSchema } from './system-config';

const LocaleSchema = z.string().min(2).max(16);

const SlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_-]+$/, 'lowercase letters / digits / underscore / hyphen');

export const APP_DEDICATED_CONFIG_MODULES = [
  'home_sections',
  'faq',
  'shelf_recommendations',
  'banner_slides',
] as const;
export const AppDedicatedConfigModuleSchema = z.enum(APP_DEDICATED_CONFIG_MODULES);
export type AppDedicatedConfigModule = z.infer<typeof AppDedicatedConfigModuleSchema>;

const AppDedicatedConfigIdentitySchema = z
  .object({
    module: AppDedicatedConfigModuleSchema,
    app_id: z.string().uuid(),
    language: LanguageCodeSchema,
  })
  .strict();

/**
 * 建立独立配置的两种方式（A2 §0.4 `init_mode`，闭集两值）。
 * `copy_global` = 复制该语言全局配置整份快照；`empty` = 直建空身份行、不写内容行。
 */
export const APP_DEDICATED_CONFIG_INIT_MODES = ['copy_global', 'empty'] as const;
export const AppDedicatedConfigInitModeSchema = z.enum(APP_DEDICATED_CONFIG_INIT_MODES);
export type AppDedicatedConfigInitMode = z.infer<typeof AppDedicatedConfigInitModeSchema>;

/**
 * POST /ops/app-dedicated-configs — 建立独立配置（复制 / 直建，A2 §0.4）。
 *
 * `init_mode` 缺省 `copy_global`（保持既有调用方语义）。两个取值只在「要不要搬内容」上分叉：
 * 身份键、`module` 闭集、响应壳、`config_source` 判据与移除语义一律相同。
 */
export const AppDedicatedConfigRequestSchema = AppDedicatedConfigIdentitySchema.extend({
  init_mode: AppDedicatedConfigInitModeSchema.optional(),
}).strict();
export type AppDedicatedConfigRequest = z.infer<typeof AppDedicatedConfigRequestSchema>;

/** 身份键（不含 `init_mode`）—— 锁 / 查重 / audit / 移除端点共用。 */
export type AppDedicatedConfigIdentity = z.infer<typeof AppDedicatedConfigIdentitySchema>;

/** DELETE /ops/app-dedicated-configs — remove a whole identity + content unit. */
export const DeleteAppDedicatedConfigQuerySchema = AppDedicatedConfigIdentitySchema;
export type DeleteAppDedicatedConfigQuery = z.infer<typeof DeleteAppDedicatedConfigQuerySchema>;

export type OpsConfigSource = 'GLOBAL' | 'GLOBAL_INHERITED' | 'APP_DEDICATED';
export type OpsConfigEnvelope<T> = {
  config_source: OpsConfigSource;
  app_id: string | null;
  language: string;
  items: T[];
};

export type RemoveAppDedicatedConfigResponse = {
  module: AppDedicatedConfigModule;
  app_id: string;
  language: string;
  removed_count: number;
};

// Home sections（app_home_sections 镜像，张培 #101 拆分 section_id 实例 id / strategy_key 策略 key）
/** 阅读偏好白名单值域（= users.reading_preference 枚举）；`[]` = 中立栏目（全员可见）*/
const ReadingPreferencesSchema = z
  .array(z.enum(['MALE_CHANNEL', 'FEMALE_CHANNEL', 'NO_PREFERENCE']))
  .max(3)
  .default([])
  .refine((arr) => new Set(arr).size === arr.length, {
    message: 'reading_preferences must be distinct',
  });

export const UpsertHomeSectionRequestSchema = z
  .object({
    /** 传 = 按 id 编辑该行；不传 → 看 section_id（App 级 override）/ 都不传 → 新建自定义栏 */
    id: z.string().uuid().optional(),
    /** null = 全局栏目；string = 该 App 自定义栏目 */
    app_id: z.string().uuid().nullable(),
    /** 当前运营语言 Tab；写入 app_home_sections.language。 */
    language: LanguageCodeSchema,
    /**
     * App 级 override 全局默认栏时传该全局栏的 section_id（dev-docs §3.2「App 行按 section_id 覆盖全局行」）：
     * 无 id + 有 section_id + app_id 非空 → 复用该 key 建/改 App override 行（不生成新 id），运营可在 App 级
     * 下架/改写 weekly_featured 等默认栏。新建普通自定义栏时不传（section_id 由 admin 自动生成）。
     */
    section_id: SlugSchema.optional(),
    /** 服务端推荐策略 key；shape 在此校验，是否 ∈ 注册表 enabled 由受控写 service 二次校验 */
    strategy_key: SlugSchema,
    title_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(500)),
    reading_preferences: ReadingPreferencesSchema,
    display_order: z.number().int().nonnegative().default(0),
    is_active: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    const titleLanguages = Object.keys(value.title_i18n);
    const title = value.title_i18n[value.language];
    if (titleLanguages.length !== 1 || titleLanguages[0] !== value.language || !title?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['title_i18n'],
        message: 'title_i18n must contain exactly the current language key with a non-blank value',
      });
    }
  });
export type UpsertHomeSectionRequest = z.infer<typeof UpsertHomeSectionRequestSchema>;

/** 首页栏目布局闭集；横排最多 10 本，竖排最多 5 本。 */
export const HomeSectionLayoutSchema = z.enum(['HORIZONTAL', 'VERTICAL']);
export type HomeSectionLayout = z.infer<typeof HomeSectionLayoutSchema>;

/** PATCH /ops/home-sections/:id — 固定三栏，仅修改展示配置。 */
export const UpdateHomeSectionRequestSchema = z
  .object({
    layout: HomeSectionLayoutSchema.optional(),
    title_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(500)).optional(),
    reading_preferences: z
      .array(z.enum(['MALE_CHANNEL', 'FEMALE_CHANNEL', 'NO_PREFERENCE']))
      .max(3)
      .refine((arr) => new Set(arr).size === arr.length, {
        message: 'reading_preferences must be distinct',
      })
      .optional(),
    display_order: z.number().int().nonnegative().optional(),
    is_active: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, { message: 'at least one field is required' });
export type UpdateHomeSectionRequest = z.infer<typeof UpdateHomeSectionRequestSchema>;

/**
 * 推荐策略注册表单行（GET /api/v1/ops/home-section-strategies 响应元素）。
 * 契约见 `02-API契约/A2-运营配置.md` §1；SSOT = `03-数据字典/70-产品配置.md` §3.2.1。
 * 返回全集（含 enabled=false 行）供 UI 区分「可选 / 已下线」两态。
 */
export type HomeSectionStrategyResponse = {
  strategy_key: string;
  /** true = ADM-OPS-S001 全局默认 3 栏的策略（server baseline seed 预置）*/
  is_default_seed: boolean;
  /** true = server 已实现取数 dispatch、admin 可保存引用；false = 已下线 */
  enabled: boolean;
};

export type HomeSectionResponse = {
  layout: HomeSectionLayout;
  id: string;
  app_id: string | null;
  language: string;
  /** 栏目实例稳定 id（自动生成）；默认 3 栏 = 同名 strategy_key */
  section_id: string;
  /** 服务端推荐策略 key（∈ 注册表）*/
  strategy_key: string;
  title_i18n: Record<string, string>;
  reading_preferences: ('MALE_CHANNEL' | 'FEMALE_CHANNEL' | 'NO_PREFERENCE')[];
  /** server baseline seed 预置的全局默认栏（禁删）；admin 不可改 */
  is_seed_baseline: boolean;
  display_order: number;
  is_active: boolean;
};

export type HomeSectionsResponse = OpsConfigEnvelope<HomeSectionResponse>;

export const ListHomeSectionsQuerySchema = z.object({
  app_id: z.string().uuid().optional(),
  language: LanguageCodeSchema,
});
export type ListHomeSectionsQuery = z.infer<typeof ListHomeSectionsQuerySchema>;

/** POST /ops/home-sections/:id/novels（A2 §2.6）*/
export const CreateHomeSectionNovelRequestSchema = z
  .object({
    novel_id: z.string().uuid(),
    display_order: z.number().int().nonnegative().optional(),
  })
  .strict();
export type CreateHomeSectionNovelRequest = z.infer<typeof CreateHomeSectionNovelRequestSchema>;

/** PATCH /ops/home-sections/:id/novels/:binding_id（A2 §2.7）— 仅 display_order */
export const UpdateHomeSectionNovelRequestSchema = z
  .object({
    display_order: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateHomeSectionNovelRequest = z.infer<typeof UpdateHomeSectionNovelRequestSchema>;

export const ListHomeSectionNovelsQuerySchema = z.object({
  locale: LocaleSchema.optional(),
});
export type ListHomeSectionNovelsQuery = z.infer<typeof ListHomeSectionNovelsQuerySchema>;

export type HomeSectionNovelResponse = {
  id: string;
  home_section_id: string;
  novel_id: string;
  /** 读侧派生 = novels.locale，表无此列 */
  locale: string;
  display_order: number;
  created_at: string;
  updated_at: string;
};

// Banners（C 端 own 镜像，#104 / server #696）：每行带 language；选层由 app_dedicated_configs 身份行决定
/** banner slug snake_case（对齐 canonical CHECK `^[a-z0-9][a-z0-9_]*$`，区别于 faq 的 kebab-case） */
const BannerSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z0-9][a-z0-9_]*$/,
    'snake_case: lowercase letters / digits / underscore, no leading underscore',
  );

export const ListBannersQuerySchema = z.object({
  app_id: z.string().uuid().optional(),
  language: LanguageCodeSchema,
});
export type ListBannersQuery = z.infer<typeof ListBannersQuerySchema>;

export const ListBannerSlidesQuerySchema = z.object({
  language: LanguageCodeSchema,
});
export type ListBannerSlidesQuery = z.infer<typeof ListBannerSlidesQuerySchema>;

/**
 * 新建 Banner 位（POST /api/v1/ops/banners，A2 §3.2）。
 *
 * 与 `UpsertBannerRequestSchema` 的差别是**语义而非字段裁剪**：本 schema 无 `id`（新建不寻址既有行）、
 * 无 `is_active`（§3.2「**服务端定**：`is_active=false`」——创建后默认下架，`ADM-OPS-S003` 的
 * 「先建后审再上架」治理流，与 FAQ §9.2 同口径），`slug` 由可选转必填（§3.2 Request 表标必填，
 * 由运营录入；**服务端定**只剩 `is_active` / `is_seed_baseline`）。
 */
export const CreateBannerRequestSchema = z
  .object({
    /** §3.2 该列必填栏是「否」：省略与 `null` 同义 = 写全局层（同 §8.2） */
    app_id: z.string().uuid().nullish(),
    /**
     * §3.2 标必填：`banners.language` 是 **NOT NULL**（`03-数据字典/70-产品配置.md` §3.3.1），
     * 两层同样必填。全局层 = `app_id` 为 `null` **且** `language` 非空 —— 不存在「语言为空的
     * 全局行」（`01-通用约定/85-运营配置维度模型.md` §5），英语全局位与波兰语全局位是两行。
     */
    language: LanguageCodeSchema,
    slug: BannerSlugSchema,
    name: z.string().min(1).max(128),
    display_order: z.number().int().nonnegative().optional(),
  })
  .strict();
export type CreateBannerRequest = z.infer<typeof CreateBannerRequestSchema>;

/**
 * 改 Banner 位 / 上下架（PATCH /api/v1/ops/banners/:id，A2 §3.3）：`name` / `display_order` /
 * `is_active` **任意子集**；省略 = service 保留既有（合并在事务锁内对已锁行做，不做 read-modify-write）。
 * **不可改**：`app_id` / `language` / `slug` / `is_seed_baseline` —— 层身份由 `:id` 所在行自身决定，
 * 故本端点不收层入参（同 §8.3 / §5.3 的「改的是哪一层由 `:id` 所在行决定」）。
 */
export const UpdateBannerRequestSchema = z
  .object({
    name: z.string().min(1).max(128).optional(),
    display_order: z.number().int().nonnegative().optional(),
    is_active: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) => v.name !== undefined || v.display_order !== undefined || v.is_active !== undefined,
    { message: 'at least one field is required' },
  );
export type UpdateBannerRequest = z.infer<typeof UpdateBannerRequestSchema>;

export type BannerResponse = {
  id: string;
  app_id: string | null;
  language: string;
  slug: string;
  name: string;
  /** server seed 预置的全局默认位（禁删）；admin 不可改 */
  is_seed_baseline: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  /** 最近更新时间 ISO 8601（banners.updated_at；ADM-OPS Banner 列表「更新」列接真）*/
  updated_at: string;
};

export type BannersResponse = OpsConfigEnvelope<BannerResponse>;

// Banner slides（C 端 own 镜像，#104 / #228 / server #696）：每帧自带 language，title_i18n 单 key 或 `{}`
// （标题选填，无标题唯一表示 = `{}`；85 §3.2.1 选填池例外，dev-docs#1534）
/** banner_slides 跳转目标类型闭集（= canonical target_type CHECK book/offer/web）。#228 决议 3：topup 废除→offer */
export const BANNER_TARGET_TYPES = ['book', 'offer', 'web'] as const;
const BannerTargetTypeSchema = z.enum(BANNER_TARGET_TYPES);

/** banner_slides web 打开方式闭集（= canonical open_mode CHECK，仅 target_type=web 生效）。#304 */
export const BANNER_OPEN_MODES = ['in_app', 'external'] as const;
const BannerOpenModeSchema = z.enum(BANNER_OPEN_MODES);

/**
 * `target_value` 按 `target_type` 分型校验的**共用实现**（A2 §4.2 + 数据字典 §3.3.2）。
 *
 * 抽出来是因为 POST / PATCH / 旧 PUT 三个入口必须逐字同一口径：任一处放宽，写进库的就是 C 端
 * serving 侧会静默丢弃的死配置。`type` 在 PATCH 下可能来自库内既有行（请求只改 `target_value`
 * 时不带 `target_type`），故这里收的是**已解析出的 type**，不是整个请求体。
 */
export function issueIfTargetValueInvalid(
  type: (typeof BANNER_TARGET_TYPES)[number],
  value: string,
): string | null {
  if (type === 'book') {
    return z.string().uuid().safeParse(value).success
      ? null
      : 'book target_value must be a novel UUID';
  }
  if (type === 'offer') {
    // offer → app_payment_offers.id（对外 offer_id，UUID v7，§3.4）。「该 offer 是否为本 App active 行」
    // 由受控写 banner-admin-service 二次校验，此处只校格式。
    return z.string().uuid().safeParse(value).success
      ? null
      : 'offer target_value must be an app_payment_offers UUID (offer_id)';
  }
  // web → https URL only（协议白名单，防 open redirect / javascript: / data: 危险协议）
  return /^https:\/\/\S+$/i.test(value) ? null : 'web target_value must be an https:// URL';
}

/**
 * 新建轮播图（POST /api/v1/ops/banners/:banner_id/slides，A2 §4.2）。
 * 无 `id`；无 `is_active`（§4.2「**服务端定**：`is_active=false`」）。其余字段与校验口径同 upsert。
 * `title_i18n` 选填：省略等同 `{}`（由 service 落 `{}`）；`null` 不收（record 不 nullable → 422）。
 */
export const CreateBannerSlideRequestSchema = z
  .object({
    language: LanguageCodeSchema,
    title_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(500)).optional(),
    image_r2_key: z
      .string()
      .min(1)
      .max(255)
      .regex(
        /^banners\/[^/]+\/slides\/[^/]+\.(webp|jpe?g|png)$/,
        'image_r2_key must be banners/{banner_id}/slides/{slide_id}.{webp|jpg|png}',
      ),
    target_type: BannerTargetTypeSchema,
    target_value: z.string().min(1).max(512),
    open_mode: BannerOpenModeSchema.nullish(),
    display_order: z.number().int().nonnegative().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const message = issueIfTargetValueInvalid(v.target_type, v.target_value);
    if (message) ctx.addIssue({ code: 'custom', path: ['target_value'], message });
    // 有标题时单 key = 本行 language 且非空白；`{}` = 无标题（选填池例外）
    const keys = v.title_i18n === undefined ? [] : Object.keys(v.title_i18n);
    if (
      keys.length > 0 &&
      (keys.length !== 1 || keys[0] !== v.language || !v.title_i18n?.[v.language]?.trim())
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['title_i18n'],
        message:
          'title_i18n must be {} or contain exactly the current language key with a non-blank value',
      });
    }
  });
export type CreateBannerSlideRequest = z.infer<typeof CreateBannerSlideRequestSchema>;

/**
 * 改轮播图 / 上下架（PATCH /api/v1/ops/banners/:banner_id/slides/:slide_id，A2 §4.3）：任意子集。
 *
 * **不收 `language`**（§4.3「**不可改**：`language` —— 改它等于把该行搬到另一语言；换语言走新建」）。
 * 由此 `title_i18n` 的「单 key 且等于本行 language」只能拿库内既有 `language` 校验 → 放在 service，
 * 不在 Zod（Zod 看不到那一行）。
 *
 * **`target_type` 与 `target_value` 必须同传或同不传**：只改其一时分型校验会落在错误的一侧
 * —— 只给 value（用旧 type 判）或只给 type（旧 value 未必符合新 type）都可能写出 C 端会静默丢弃的
 * 死链。同传即可在 Zod 里完整判定，无需回库。
 */
export const UpdateBannerSlideRequestSchema = z
  .object({
    title_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(500)).optional(),
    image_r2_key: z
      .string()
      .min(1)
      .max(255)
      .regex(
        /^banners\/[^/]+\/slides\/[^/]+\.(webp|jpe?g|png)$/,
        'image_r2_key must be banners/{banner_id}/slides/{slide_id}.{webp|jpg|png}',
      )
      .optional(),
    target_type: BannerTargetTypeSchema.optional(),
    target_value: z.string().min(1).max(512).optional(),
    open_mode: BannerOpenModeSchema.nullish(),
    display_order: z.number().int().nonnegative().optional(),
    is_active: z.boolean().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    // **两者可各改各的**（§4.3 = 任意子集）：只给 target_value（换一本书）或只给 target_type
    // 都合法。分型校验因此**不能在 Zod 做** —— 缺的那一半在库里，Zod 看不到那一行；
    // 由 banner-admin-service 取「合并后的生效值」再校（见 writeBannerSlide）。
    // 两者都给时可以就地判完，省一次无谓的落库前失败。
    if (v.target_type !== undefined && v.target_value !== undefined) {
      const message = issueIfTargetValueInvalid(v.target_type, v.target_value);
      if (message) ctx.addIssue({ code: 'custom', path: ['target_value'], message });
    }
    if (v.title_i18n !== undefined) {
      const keys = Object.keys(v.title_i18n);
      const only = keys.length === 1 ? v.title_i18n[keys[0] as string] : undefined;
      // `{}` = 清空标题（§4.3；省略才是保持原值）。
      // 「唯一那个 key 等于本行 language」在 service 校（Zod 看不到库内 language）；
      // **非空白**必须在这里判：`min(1)` 收得下 `"   "`，而 POST 用 trim 判过 ——
      // 漏掉它等于 PATCH 有一条绕过该规则的路，最终撞库上的 CHECK 变成 500 而不是 422。
      if (keys.length > 0 && (keys.length !== 1 || !only?.trim())) {
        ctx.addIssue({
          code: 'custom',
          path: ['title_i18n'],
          message:
            'title_i18n must be {} or contain exactly one language key with a non-blank value',
        });
      }
    }
    if (
      v.title_i18n === undefined &&
      v.image_r2_key === undefined &&
      v.target_type === undefined &&
      v.target_value === undefined &&
      v.open_mode === undefined &&
      v.display_order === undefined &&
      v.is_active === undefined
    ) {
      ctx.addIssue({ code: 'custom', path: [], message: 'at least one field is required' });
    }
  });
export type UpdateBannerSlideRequest = z.infer<typeof UpdateBannerSlideRequestSchema>;

/**
 * `offer_serving_state` 闭集（A2 §4.1）：admin 读侧派生的「该 offer slide 当前是否真的在 C 端展示」。
 * 判据与 C 端 serving 过滤同源（dev-docs `03-数据字典/70-产品配置.md` §3.3.3）：
 *   SERVING=满足全部判据正在展示 / NOT_STARTED=now < starts_at / ENDED=now >= ends_at（半开区间）/
 *   OFFER_UNAVAILABLE=被引 offer 不存在·非 active·已软删·跨 App。
 * 存在意义：写侧列表只显示 is_active（运营主动上下架），运营看不出「窗口外自动不展示」——本字段补上该事实。
 */
export const OFFER_SERVING_STATES = [
  'SERVING',
  'NOT_STARTED',
  'ENDED',
  'OFFER_UNAVAILABLE',
] as const;
export type OfferServingState = (typeof OFFER_SERVING_STATES)[number];

export type BannerSlideResponse = {
  id: string;
  banner_id: string;
  language: string;
  title_i18n: Record<string, string>;
  image_r2_key: string;
  /**
   * 读侧图片 URL（A2 §4.1，复用 A7 §2.2 反馈附件口径 = admin 鉴权代理相对路径，非 S3 签名 URL）：
   * 指向 GET /api/v1/ops/banners/:banner_id/slides/:slide_id/image。JWT 门后端点——前端不能裸
   * <img src>（不带 Authorization），须 authedFetch → blob → objectURL 渲染。
   */
  image_url: string;
  target_type: (typeof BANNER_TARGET_TYPES)[number];
  target_value: string;
  /** web 打开方式（仅 target_type=web；#304）：in_app/external；非 web 或未配 → null（serving 默认 external）*/
  open_mode: (typeof BANNER_OPEN_MODES)[number] | null;
  display_order: number;
  is_active: boolean;
  /**
   * 派生只读字段（A2 §4.1）：**仅 `target_type='offer'` 行下发**（book / web 行本键缺席，非 null）。
   * 每次请求实时求值、不落库；**不改写 `is_active`**——slide 上下架仍只由运营显式操作决定，
   * 本字段只描述「当前是否真的在展示」，让运营区分「我主动下架了」与「活动窗口外自动不展示」。
   */
  offer_serving_state?: OfferServingState;
};

// FAQ canonical（C 端 own 镜像，#221 / dev-docs 03-数据字典/90-反馈 §3；REST CRUD，取代旧 PUT upsert 单数 /faq）：
//  - global ∪ per-App override：app_id null=全局默认层 / 非空=该 App 覆盖·追加层（镜像 banners / app_home_sections 范式）。
//  - category_key FK→faq_categories.key（分类升级为可配置注册表，去 feedback.category enum 锁）；serving category_title 从 faq_categories.title_i18n 渲染。
//  - question/answer/category title i18n：**en 必填非空**（对齐 canonical faq/faq_categories「en 必填」，区别 home-sections 的 zh+en）。
//  - is_seed_baseline server 拥有（admin 只读、禁写）；软删（deleted_at）；slug 深链 kebab、上线后禁改（PATCH 保留既有）。

/** faq slug kebab-case（hyphen-only，对齐 canonical CHECK `^[a-z0-9][a-z0-9-]*$`，区别于 banner/push 的下划线） */
const FaqSlugSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(
    /^[a-z0-9][a-z0-9-]*$/,
    'kebab-case: lowercase letters / digits / hyphen, no leading hyphen',
  );

/** faq_categories.key UPPER_SNAKE（对齐 canonical CHECK `^[A-Z][A-Z0-9_]*$`）；faq.category_key FK 锚点，创建后不可改。导出供 GET /faqs ?category_key 过滤复用 */
export const FaqCategoryKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[A-Z][A-Z0-9_]*$/,
    'UPPER_SNAKE: uppercase letters / digits / underscore, no leading digit',
  );

/**
 * FAQ i18n：**en 必填非空**（对齐 canonical faq/faq_categories「en 必填」——seeds 为 {en, pl}，非 zh），
 * 其它 locale 可选。与 home-sections I18nRequired 同口径（en 必填）。
 */
const faqI18nEnRequired = (maxLen: number) =>
  z
    .record(LocaleSchema, z.string().min(1).max(maxLen))
    .refine((m) => typeof m.en === 'string' && m.en.trim().length > 0, {
      message: 'en is required and must be non-blank',
    });

/**
 * 6 条 baseline 分类 key（C 端 0016 seed：PAYMENT/CONTENT/BUG/SUGGESTION/ACCOUNT/OTHER）。
 * **仅作 web 兜底 / 参考**——权威可用分类集来自 GET /api/v1/ops/faq-categories（运营可增删 / 改名 / 排序）。
 */
export const FAQ_BASELINE_CATEGORY_KEYS = [
  'PAYMENT',
  'CONTENT',
  'BUG',
  'SUGGESTION',
  'ACCOUNT',
  'OTHER',
] as const;

/**
 * 新建 FAQ（POST /api/v1/ops/faqs）：app_id 定 scope（null=全局默认层）、slug 必填 kebab（上线后禁改）、
 * category_key 必 ∈ active faq_categories（service 校验，否则 OPS_FAQ_CATEGORY_NOT_FOUND）。
 * **is_active / is_seed_baseline 不可传**——服务端硬编码 is_active=false（新建默认下架，「先建后审再上架」治理流，§9.2）+ is_seed_baseline=false。
 */
export const CreateFaqRequestSchema = z
  .object({
    /** null = 全局默认层；string(uuid) = 该 App 覆盖 / 追加层 */
    app_id: z.string().uuid().nullable(),
    language: LanguageCodeSchema,
    slug: FaqSlugSchema,
    category_key: FaqCategoryKeySchema,
    question_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(500)),
    answer_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(4000)),
    /** 省略 → service 默认 0（同 category 内排序） */
    display_order: z.number().int().nonnegative().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    for (const field of ['question_i18n', 'answer_i18n'] as const) {
      const pool = value[field];
      const keys = Object.keys(pool);
      if (keys.length !== 1 || keys[0] !== value.language || !pool[value.language]?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `${field} must contain exactly the current language key with a non-blank value`,
        });
      }
    }
  });
export type CreateFaqRequest = z.infer<typeof CreateFaqRequestSchema>;

/**
 * 编辑 FAQ（PATCH /api/v1/ops/faqs/:id）：slug / app_id / is_seed_baseline 不可改（省略——deep-link 稳定 + scope pin）；
 * category_key / 问答 / 排序 / 上下架可改（省略 = service 保留既有，不静默改）；至少传一个字段。
 */
export const UpdateFaqRequestSchema = z
  .object({
    category_key: FaqCategoryKeySchema.optional(),
    question_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(500)).optional(),
    answer_i18n: z.record(LanguageCodeSchema, z.string().min(1).max(4000)).optional(),
    display_order: z.number().int().nonnegative().optional(),
    is_active: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.category_key !== undefined ||
      v.question_i18n !== undefined ||
      v.answer_i18n !== undefined ||
      v.display_order !== undefined ||
      v.is_active !== undefined,
    { message: 'at least one field is required' },
  );
export type UpdateFaqRequest = z.infer<typeof UpdateFaqRequestSchema>;

export type FaqResponse = {
  id: string;
  /** null = 全局默认层；string = 该 App 覆盖 / 追加层 */
  app_id: string | null;
  language: string;
  slug: string;
  /** FK→faq_categories.key（分类稳定键；category_title 由 faq_categories.title_i18n 渲染，不在本行）*/
  category_key: string;
  question_i18n: Record<string, string>;
  answer_i18n: Record<string, string>;
  /** server baseline seed 预置（禁删）；admin 只读 */
  is_seed_baseline: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type FaqsResponse = OpsConfigEnvelope<FaqResponse>;

export const ListFaqsQuerySchema = z.object({
  app_id: z.string().uuid().optional(),
  language: LanguageCodeSchema,
  category_key: FaqCategoryKeySchema.optional(),
});
export type ListFaqsQuery = z.infer<typeof ListFaqsQuerySchema>;

// faq_categories 分类注册表（C 端 own 镜像，#221）：global-only 可配置分类（key 稳定 + title_i18n 多语言 + 排序 + 上下架）。
/** 分类展示名 i18n：en 必填非空（category_title 唯一渲染源）；其它 locale 可选 */
const FaqCategoryTitleI18n = faqI18nEnRequired(200);

/**
 * 新建分类（POST /api/v1/ops/faq-categories）：key 必填 UPPER_SNAKE（创建后不可改）、title_i18n（en 必填）。
 * **is_active / is_seed_baseline 不可传**——服务端硬编码 is_active=false（新建默认下架，§10.2）+ is_seed_baseline=false。
 */
export const CreateFaqCategoryRequestSchema = z
  .object({
    key: FaqCategoryKeySchema,
    title_i18n: FaqCategoryTitleI18n,
    /** 省略 → service 默认 0（category 级排序，升序）*/
    display_order: z.number().int().nonnegative().optional(),
  })
  .strict();
export type CreateFaqCategoryRequest = z.infer<typeof CreateFaqCategoryRequestSchema>;

/**
 * 编辑分类（PATCH /api/v1/ops/faq-categories/:id）：key 创建后不可改（省略——faq.category_key FK 锚点）；
 * title_i18n / 排序 / 上下架可改（省略 = 保留既有）；至少传一个字段。
 */
export const UpdateFaqCategoryRequestSchema = z
  .object({
    title_i18n: FaqCategoryTitleI18n.optional(),
    display_order: z.number().int().nonnegative().optional(),
    is_active: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) => v.title_i18n !== undefined || v.display_order !== undefined || v.is_active !== undefined,
    { message: 'at least one field is required' },
  );
export type UpdateFaqCategoryRequest = z.infer<typeof UpdateFaqCategoryRequestSchema>;

export type FaqCategoryResponse = {
  id: string;
  key: string;
  title_i18n: Record<string, string>;
  display_order: number;
  is_active: boolean;
  /** server baseline seed 预置（6 条基线分类，禁删）；admin 只读 */
  is_seed_baseline: boolean;
  created_at: string;
  updated_at: string;
};

// S006「App 运营配置初始化」已弃用（dev-docs ADM-OPS-S006「弃用」）：home_sections（#101）+ banners（#104）
// 双双归 C 端 global server-seeded，App 不做自动初始化。endpoint / schema / web hook 一并退役（codex adversarial #23）。

// 运营弹窗场景（ADM-OPS-S007 / S008）—— app_operational_popup_scenes（C 端表，scenario 闭集见 §3.1）
/**
 * 运营弹窗场景闭集（与 C 端 app_operational_popup_scenes scenario CHECK 一致）。
 * new_user_offer 已迁出本闭集（#142 PR142-002：改为独立支付页 scenario，归 PAYMENT_SCENARIOS）。
 * 三类被动触发的 popup product slot；insufficient_balance_offer（余额不足优惠，dev-docs #376）=
 * 阅读器解锁场景、面向已支付用户（刷余额仍不足 + 已非首充资格）的补充转化弹窗。
 */
export const OperationalPopupScenarioSchema = z.enum([
  'payment_exit_retention',
  'first_purchase_offer',
  'insufficient_balance_offer',
]);
export type OperationalPopupScenario = z.infer<typeof OperationalPopupScenarioSchema>;

/**
 * 新建运营弹窗场景（POST /api/v1/ops/popup-scenes，A2 §5.2）。
 *
 * 无 `id`；无 `is_active`（§5.2「**服务端定**：`is_active=false`」）；**不收 `language`**
 * （§5 头部：本模块无语言维度，一个 App 的上下架对全部语言一致）；**不收 `access_level_id`**
 * （§5「本页禁止项：不得接受、不得回写」——唯一来源是 `system_popup_access_levels`，写入面在
 * `A5 §11`，admin 侧入口在系统设置 → 弹窗权限档）。`.strict()` 使传入任一即 `VALIDATION_FAILED`。
 *
 * `app_id` 可为 `null` = 写全局层（§5.2 字段表「UUID v7 | null，否」）；服务端按 A8 §9.0
 * 取全局层栅栏排他模式，不取任何 per-App lock。
 */
export const CreateOperationalPopupSceneRequestSchema = z
  .object({
    app_id: z.string().uuid().nullable(),
    scenario: OperationalPopupScenarioSchema,
  })
  .strict();
/**
 * `GET /api/v1/ops/popup-scenes` 的响应壳（A2 §5.1）。
 *
 * 复用 §0.3 的 `OpsConfigEnvelope` 形状，但 **`language` 回显恒 `null`** —— 本模块无语言维度
 * （§5 头部：弹窗无多语文案，其上下架对一个 App 的全部语言一致），故单独收一个类型而不是塞
 * 空串进 `OpsConfigEnvelope<T>` 的 `language: string`。
 *
 * `config_source` 的判据也与其余模块不同：**取该 App 的未软删行数**，不是 `app_dedicated_configs`
 * 身份行（本模块不建身份行）。省 `app_id` = `GLOBAL`；传且零行 = `GLOBAL_INHERITED` + 恒 `[]`
 * （**不回传全局行**）；传且有行 = `APP_DEDICATED`。
 */
/**
 * canonical 壳的元素型。`app_id` 的可空性已并入 `OperationalPopupSceneResponse`（全局层可写后
 * 读写两侧形态一致），故这里是同义别名 —— 保留名字是因为响应壳的字段语义仍值得单独称呼。
 */
export type PopupSceneEnvelopeItem = OperationalPopupSceneResponse;

export type PopupScenesResponse = {
  config_source: OpsConfigSource;
  app_id: string | null;
  language: null;
  items: PopupSceneEnvelopeItem[];
};

export type CreateOperationalPopupSceneRequest = z.infer<
  typeof CreateOperationalPopupSceneRequestSchema
>;

/**
 * 改场景上下架（PATCH /api/v1/ops/popup-scenes/:id，A2 §5.3）：契约写「**Request**：仅 `is_active`」。
 * **不可改**：`app_id`（改它等于跨层搬行）/ `scenario`（「改的是哪一层由 `:id` 所在行自身的
 * `app_id` 决定」）/ `access_level_id`（§5 本页禁止项，唯一来源见 A5 §11）。
 */
export const UpdateOperationalPopupSceneRequestSchema = z
  .object({
    is_active: z.boolean(),
  })
  .strict();
export type UpdateOperationalPopupSceneRequest = z.infer<
  typeof UpdateOperationalPopupSceneRequestSchema
>;

/**
 * 运营弹窗场景行（A2 §5.1 Response 字段清单：id / app_id / scenario / is_active / created_at /
 * updated_at）。**不含 `access_level_id`** —— 该值全系统一份，唯一来源是 `system_popup_access_levels`
 * （A5 §11），本表那一列在 C 端兼容窗口内仍存在但已不是事实源，故不进本响应。
 */
export type OperationalPopupSceneResponse = {
  id: string;
  /**
   * `null` = 全局层行（§5.1 GLOBAL 分支）。全局层可写后，POST / PATCH / DELETE 的「返完整行」
   * 同样可能是全局层行 —— 故读写两侧共用一个可空型，不再分列两型。
   */
  app_id: string | null;
  scenario: OperationalPopupScenario;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

// 运营弹窗 paywall placement（ADM-OPS-S008）—— app_payment_methods 中 scenario='operational_popup' 的一行，
// 承载 adapty_placement_id。它是 ADM-OPS-S007 各 active 运营弹窗场景的唯一 serving 落点（C 端 GET /payment/popups
// 按 force_third_party_payment 选中通道命中该行的 adapty_placement_id）。本组只读（A2 §6.1）：写入 / 清空的
// 唯一入口是支付页 PUT /payment-pages/:id/paywalls 的 operational_popup 分支（A8 §7.5，UpsertPaywallRequestSchema）。
/**
 * placement 通道闭集 = **结算通道层**（`03-数据字典/70-产品配置.md` §3 两层闭集；对齐
 * `app_payment_methods` active CHECK）。
 */
export const PopupPlacementPaymentMethodSchema = SettlementChannelSchema;
export type PopupPlacementPaymentMethod = z.infer<typeof PopupPlacementPaymentMethodSchema>;

export type PopupPlacementResponse = {
  id: string;
  app_id: string;
  payment_method: PopupPlacementPaymentMethod;
  scenario: 'operational_popup';
  /** `null` = 该渠道未配置，不参与 serving 候选（A2 §6.1）。 */
  adapty_placement_id: string | null;
  created_at: string;
  updated_at: string;
};

// 书架推荐（ADM-OPS-S009 / A2 §8）：两层承载。app_id null = 全局层；非空 = 该 App 独立配置层。
// novel_id 是 per-locale 行原值（不做书组键规范化）。推荐语选填，加入与上架均不校验（§8.5）。

/**
 * 加入推荐（POST /api/v1/ops/shelf-recommendations，A2 §8.2）。
 *
 * 无 `id`。`sort_order` 转可选（§8.2「缺省 `0`」）；`is_listed` 可选、缺省 `false`
 * （§8.2「新建默认 `is_listed=false`；POST body 显式带 `is_listed=true` 则直接上架」——故本字段**收**，
 * 只是不必填）。
 *
 * 与旧 PUT 的关键差别：**同层撞键返 `RESOURCE_CONFLICT`、不静默回退成编辑**
 * （§8.2「同层重复加入 → `RESOURCE_CONFLICT`」）。
 */
export const CreateShelfRecommendationRequestSchema = z
  .object({
    /**
     * §8.2「**省略** / `null` = 写全局层」—— 两者等价，故是 `.nullish()` 而不是 `.nullable()`：
     * 后者要求这个键**必须出现**，只带 `language` + `novel_id` 的合法全局层 POST 会在进 service
     * 之前就被 VALIDATION_FAILED 拒掉。
     */
    app_id: z.string().uuid().nullish(),
    language: LanguageCodeSchema,
    novel_id: z.string().uuid(),
    sort_order: z.number().int().min(0).optional(),
    is_listed: z.boolean().optional(),
  })
  .strict();
export type CreateShelfRecommendationRequest = z.infer<
  typeof CreateShelfRecommendationRequestSchema
>;

/**
 * 改排序 / 上下架（PATCH /api/v1/ops/shelf-recommendations/:id，A2 §8.3）：
 * `sort_order` / `is_listed` **任意子集**。**不可改**：`app_id`（决定所属层）/ `novel_id`
 * （决定配置对象与逻辑语言段）——「改的是哪一层由 `:id` 所在行自身决定」，本端点不收层入参。
 */
export const UpdateShelfRecommendationRequestSchema = z
  .object({
    sort_order: z.number().int().min(0).optional(),
    is_listed: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.sort_order !== undefined || v.is_listed !== undefined, {
    message: 'at least one field is required',
  });
export type UpdateShelfRecommendationRequest = z.infer<
  typeof UpdateShelfRecommendationRequestSchema
>;

export type ShelfRecommendationResponse = {
  id: string;
  /** null = 全局层行 */
  app_id: string | null;
  novel_id: string;
  sort_order: number;
  is_listed: boolean;
  created_at: string;
  updated_at: string;
  // JOIN novels（供 UI 展示推荐语）：
  /** novels.title（JOIN 命中行；novel 不存在/已删 → null）*/
  novel_title: string | null;
  /** novels.cover_url */
  cover_url: string | null;
  /** novels.recommendation_text_i18n 多语言池：UI 据此展示该书行自身语言的推荐语。*/
  recommendation_text_i18n: Record<string, string> | null;
};

export type ShelfRecommendationsResponse = OpsConfigEnvelope<ShelfRecommendationResponse>;

export const ListShelfRecommendationsQuerySchema = z.object({
  app_id: z.string().uuid().optional(),
  language: LanguageCodeSchema,
});
export type ListShelfRecommendationsQuery = z.infer<typeof ListShelfRecommendationsQuerySchema>;

/** GET /ops/config-language-tabs 的 module 闭集（85 §6 / A2 §0.2）*/
export const ConfigLanguageTabModules = [
  'home_sections',
  'faq',
  'shelf_recommendations',
  'banner_slides',
] as const;
export const ConfigLanguageTabModuleSchema = z.enum(ConfigLanguageTabModules);
export type ConfigLanguageTabModule = z.infer<typeof ConfigLanguageTabModuleSchema>;

export const ConfigLanguageTabNotSelectableReasons = [
  'APP_LANGUAGE_NOT_ALLOWED',
  'GLOBAL_CONFIG_NOT_READY',
] as const;
export const ConfigLanguageTabNotSelectableReasonSchema = z.enum(
  ConfigLanguageTabNotSelectableReasons,
);
export type ConfigLanguageTabNotSelectableReason = z.infer<
  typeof ConfigLanguageTabNotSelectableReasonSchema
>;

export const ConfigLanguageTabsQuerySchema = z
  .object({
    app_id: z.string().uuid().optional(),
    module: ConfigLanguageTabModuleSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.app_id && value.module === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['module'],
        message: 'module is required when app_id is set',
      });
    }
  });
export type ConfigLanguageTabsQuery = z.infer<typeof ConfigLanguageTabsQuerySchema>;

export const ConfigLanguageTabItemSchema = z.object({
  language: z.string().min(2).max(8),
  is_selectable: z.boolean(),
  not_selectable_reason: ConfigLanguageTabNotSelectableReasonSchema.nullable(),
});
export type ConfigLanguageTabItem = z.infer<typeof ConfigLanguageTabItemSchema>;

export const ConfigLanguageTabsResponseSchema = z.object({
  app_id: z.string().uuid().nullable(),
  module: ConfigLanguageTabModuleSchema.nullable(),
  default_language: z.literal('en'),
  tabs: z.array(ConfigLanguageTabItemSchema),
});
export type ConfigLanguageTabsResponse = z.infer<typeof ConfigLanguageTabsResponseSchema>;
